import { HttpEvent, HttpHandlerFn, HttpHeaders, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { Router } from '@angular/router';
import {
  SSO_LOGOUT_IN_PROGRESS_KEY,
  SSO_REDIRECT_IN_PROGRESS_KEY,
  SSO_RETURN_URL_KEY,
  SSO_TOKEN_REFRESH_REQUIRED_KEY,
} from '@core/sso/sso.constants';
import { AppStore } from '@services/store/app.store';
import { Console } from '@shared/providers/custom-logger';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from '../services/auth.service';
import { LoadingService } from '../services/loading.service';
import { UserService } from '../services/user.service';

/** Header sulla richiesta ritentata dopo refresh token legacy (evita loop infinito su 401). */
const HTTP_AUTH_RETRY_HEADER = 'X-Auth-Retry';

const PUBLIC_ROUTES: readonly string[] = [
  '/',
  '/manifestazione',
  '/convenzionato',
  '/promotore',
  '/login',
  '/signup',
  '/recovery',
  '/intro',
  '/app-download',
];

const EXCLUDED_ENDPOINTS: readonly string[] = [
  'login',
  'logout',
  'refresh-token',
  'sso/exchange-ticket',
  'sso/exchange-ticket-no-profile',
  'user/registrazione/disponibile',
  'user/registrazione/verifica',
  'user/registrazione',
  'password/email',
  'password/reset',
];

function getCurrentPath(url: string): string {
  return (url || '/').split('?')[0] || '/';
}

function isSsoSession(appStore: any): boolean {
  return appStore.is_sso() === true;
}

function isPublicRoute(path: string): boolean {
  return PUBLIC_ROUTES.some((route) => route === '/' ? path === '/' : path === route || path.startsWith(`${route}/`));
}

function redirectToLegacyLogin(router: Router): never {
  router.navigateByUrl('/login', { replaceUrl: true });
  throw new Error('No token found');
}

function handleProtectedRouteWithoutToken(appStore: any, authService: AuthService, router: Router): never {
  if (appStore.is_sso() === true) {
    if (environment.ENABLE_ALERTS) {
      alert(`[app] [HTTP Interceptor] Nessun token trovato (sessione SSO) - Redirect ad Auth-App\nURL corrente: ${router.url}`);
    } else {
      Console.log('HTTP Interceptor - Nessun token (sessione SSO), redirect ad Auth-App', router.url, 'warn');
    }
    authService.redirectToAuthAppLogin(router.url);
    throw new Error('No token found');
  }
  if (environment.ENABLE_ALERTS) {
    alert(`[app] [HTTP Interceptor] Nessun token (modalità legacy) - Redirect a /login\nURL corrente: ${router.url}`);
  } else {
    Console.log('HTTP Interceptor - Nessun token (legacy), redirect a /login', router.url, 'warn');
  }
  redirectToLegacyLogin(router);
}

function handleLegacy401(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  error: any,
  appStore: any,
  authService: AuthService,
  router: Router
): Observable<HttpEvent<unknown>> {
  if (req.url.includes('refresh-token') || req.headers.get(HTTP_AUTH_RETRY_HEADER) === '1') {
    appStore.clearAuth();
    return throwError(() => {
      redirectToLegacyLogin(router);
    });
  }

  Console.log('HTTP Interceptor - 401 legacy, tentativo refresh token', { url: router.url }, 'warn');

  return authService.refreshToken().pipe(
    switchMap(() => {
      const newToken = appStore.token();
      if (!newToken?.token) {
        appStore.clearAuth();
        return throwError(() => {
          redirectToLegacyLogin(router);
        });
      }
      const retried = req.clone({
        setHeaders: {
          Authorization: `Bearer ${newToken.token}`,
          Accept: 'application/json',
          [HTTP_AUTH_RETRY_HEADER]: '1',
        },
      });
      return next(retried);
    }),
    catchError((refreshErr) => {
      appStore.clearAuth();
      router.navigateByUrl('/login', { replaceUrl: true });
      return throwError(() => refreshErr);
    })
  );
}

function handleSso401(error: any, appStore: any, authService: AuthService, router: Router): Observable<never> {
  const hadToken = !!appStore.token();
  const hasUser = !!appStore.user();
  let isTokenRefreshRequired = sessionStorage.getItem(SSO_TOKEN_REFRESH_REQUIRED_KEY) === 'true';
  if (!isTokenRefreshRequired && (hadToken || hasUser)) {
    isTokenRefreshRequired = true;
    sessionStorage.setItem(SSO_TOKEN_REFRESH_REQUIRED_KEY, 'true');
  }

  Console.log('HTTP Interceptor - 401 SSO', {
    isTokenRefreshRequired,
    hadToken,
    hasUser,
    currentUrl: router.url,
    redirectPath: isTokenRefreshRequired ? 'fast-refresh' : 'login',
  }, 'warn');

  appStore.clearAuth();
  sessionStorage.setItem(SSO_REDIRECT_IN_PROGRESS_KEY, 'true');

  const redirectPath = isTokenRefreshRequired ? 'fast-refresh' : 'login';
  const redirectUrl = authService.buildAuthAppRedirectUrl(redirectPath);

  if (environment.ENABLE_ALERTS) {
    alert(`[app] [HTTP Interceptor] 401 SSO - Redirect ad Auth-App (${redirectPath})\nURL: ${router.url}\nRedirect a: ${redirectUrl}\nhadToken: ${hadToken} | hasUser: ${hasUser} | isTokenRefreshRequired: ${isTokenRefreshRequired}`);
  } else {
    Console.log(`HTTP Interceptor - 401 SSO, redirect ad Auth-App (${redirectPath})`, { currentUrl: router.url, redirectUrl, isTokenRefreshRequired }, 'warn');
  }

  sessionStorage.setItem(SSO_RETURN_URL_KEY, router.url);
  window.location.href = redirectUrl;
  return throwError(() => error);
}

/**
 * Interceptor HTTP globale per aggiungere il token di autenticazione e gestire errori comuni.
 * - Aggiunge l'header Authorization con il token JWT alle richieste (eccetto alcune rotte pubbliche).
 * - Gestisce il refresh automatico del token in caso di 401.
 * - Gestisce il logout automatico in caso di 403.
 * - Gestisce errori di validazione e server.
 * - Mostra automaticamente un loading dopo 500ms se la richiesta è ancora in corso.
 */
export const httpInterceptor: HttpInterceptorFn = (req, next) => {
  // Usiamo l'Injector per ottenere i servizi solo quando servono
  const injector = inject(Injector);
  const router = inject(Router);
  const appStore = inject(AppStore);
  const authService = inject(AuthService);
  const loadingService = inject(LoadingService);

  const isExcluded = EXCLUDED_ENDPOINTS.some((endpoint) => req.url.includes(endpoint));
  /**
   * Recupera il token dallo store e lo aggiunge all'header Authorization
   * solo per gli endpoint NON esclusi.
   */
  if (!isExcluded) {
    const token = appStore.token();
    
    if (!token) {
      // Se siamo in fase di logout SSO, non fare redirect: logoutToAuthApp sta già reindirizzando alla landing
      const isLogoutInProgress = sessionStorage.getItem(SSO_LOGOUT_IN_PROGRESS_KEY) === 'true';
      // Se siamo già in fase di redirect verso Auth-App (login o fast-refresh), non fare redirect multipli.
      // Questo è importante perché quando l'interceptor gestisce un 401 e fa redirect a fast-refresh,
      // pulisce il token. Se arriva un'altra richiesta HTTP senza token subito dopo, senza questo controllo
      // chiamerebbe redirectToAuthAppLogin che farebbe redirect a /login invece di rispettare il redirect
      // già in corso a /sso-fast.
      const isRedirectInProgress = sessionStorage.getItem(SSO_REDIRECT_IN_PROGRESS_KEY) === 'true';
      
      if (isSsoSession(appStore) && (isLogoutInProgress || isRedirectInProgress)) {
        if (environment.ENABLE_ALERTS) {
          alert(`[app] [HTTP Interceptor] Nessun token ma redirect già in corso - Ignorato\nURL: ${router.url}\nLogout in corso: ${isLogoutInProgress ? 'SÌ' : 'NO'}\nRedirect in corso: ${isRedirectInProgress ? 'SÌ' : 'NO'}`);
        } else {
          Console.log('HTTP Interceptor - Nessun token ma redirect già in corso, ignorato', { 
            url: router.url, 
            isLogoutInProgress, 
            isRedirectInProgress 
          }, 'warn');
        }
        return throwError(() => new Error('No token found'));
      }
  
      // Controlla se siamo su una route pubblica
      const currentUrl = getCurrentPath(router.url);
      const routeIsPublic = isPublicRoute(currentUrl);
      
      // Se è richiesto un refresh del token (token scaduto), lascia passare la richiesta senza token
      // La richiesta fallirà con 401 e verrà gestita dal caso 401 che usa fast-refresh
      const isTokenRefreshRequired = sessionStorage.getItem(SSO_TOKEN_REFRESH_REQUIRED_KEY) === 'true';
      if (isTokenRefreshRequired) {
        // Token scaduto/invalidato: lascia passare la richiesta che fallirà con 401 e userà fast-refresh
        if (environment.ENABLE_ALERTS) {
          alert(`[app] [HTTP Interceptor] Nessun token ma refresh richiesto (token scaduto) - Lascia passare richiesta\nURL: ${req.url}\nLa richiesta fallirà con 401 e userà fast-refresh`);
        } else {
          Console.log('HTTP Interceptor - Nessun token ma refresh richiesto (token scaduto), lascia passare richiesta', { url: req.url }, 'info');
        }
        return next(req);
      }
      
      // Route protetta senza token: SSO → Auth-App; legacy (o is_sso non true) → /login interno
      if (!routeIsPublic) {
        return throwError(() => {
          handleProtectedRouteWithoutToken(appStore, authService, router);
        });
      }
      
      // Se siamo su una route pubblica, lascia passare la richiesta senza token
      // La richiesta probabilmente fallirà con un errore 401, che verrà gestito normalmente
      return next(req);
    }
  
    // Clona la richiesta con l'header Authorization solo se non esclusa
    req = req.clone({
      headers: new HttpHeaders({
        Authorization: `Bearer ${token.token}`,
        Accept: 'application/json',
      }),
    });
  }

  // Avvia il timer per il loading automatico
  loadingService.startLoading();

  return next(req).pipe(
    catchError((error) => {
      // Per gli endpoint esclusi (es. SSO, registrazione, reset password) non applichiamo
      // la logica speciale di gestione 401/448/449, ma propaghiamo semplicemente l'errore.
      if (isExcluded) {
        return throwError(() => error);
      }

      /**
       * Gestione errore 401 (token scaduto/non valido):
       * - Pulisce il token scaduto e l'utente dal localStorage (importante quando rememberMe è attivo).
       * - Redirect ad Auth-App (SSO) invece di refresh token.
       * - Salva returnUrl in sessionStorage per ripristinarlo dopo il login SSO.
       * - Ignora se siamo già in fase di logout o redirect (evita redirect multipli).
       */
      if (error.status === 401) {
        const authServiceInj = injector.get(AuthService);
        const appStoreInj = injector.get(AppStore);

        // Controlla se siamo già in fase di logout/redirect verso Auth-App
        // Questo evita redirect multipli quando logoutToAuthApp() ha già pulito il token e sta reindirizzando
        const isLogoutInProgress = sessionStorage.getItem(SSO_LOGOUT_IN_PROGRESS_KEY) === 'true';
        // Controlla se siamo già in fase di redirect verso Auth-App per login
        // Questo evita redirect multipli quando abbiamo già iniziato un redirect dopo un 401
        const isRedirectInProgress = sessionStorage.getItem(SSO_REDIRECT_IN_PROGRESS_KEY) === 'true';
        
        if (isSsoSession(appStoreInj) && (isLogoutInProgress || isRedirectInProgress)) {
          Console.log('HTTP Interceptor - 401 ignorato, redirect già in corso', '', 'warn');
          if (environment.ENABLE_ALERTS) {
            alert(`[app] [HTTP Interceptor] 401 ricevuto ma redirect già in corso - Ignorato\nURL corrente: ${router.url}\nLogout in corso: ${isLogoutInProgress ? 'SÌ' : 'NO'}\nRedirect in corso: ${isRedirectInProgress ? 'SÌ' : 'NO'}`);
          }
          return throwError(() => error);
        }

        if (appStoreInj.is_sso() !== true) {
          return handleLegacy401(req, next, error, appStoreInj, authServiceInj, router);
        }
        return handleSso401(error, appStoreInj, authServiceInj, router);
      }

      /**
       * Gestione errore 403 (forbidden):
       * - Effettua il logout e reindirizza al login.
       *  NON serve, gestito dalla app
       */

      /**
       * Gestione errori 448 e 449:
       * - Chiama user.service.getApiProfili() per aggiornare i profili utente.
       * - Determina il ruolo dalla chiamata API e reindirizza alla landing page corrispondente.
       */
      if (error.status === 448 || error.status === 449) {
        const userService = injector.get(UserService);
        
        // Determina il ruolo dalla chiamata API usando il metodo centralizzato
        const authService = injector.get(AuthService);
        const targetApp = authService.determineTargetApp(router.url);
        const role = ['promotore', 'convenzionato', 'manifestazione'].includes(targetApp) ? targetApp : null;
        
        return userService.getApiProfili().pipe(
          switchMap(() => {
            // Dopo aver aggiornato i profili, reindirizza alla landing page del ruolo
            if (role) {
              const landingPage = `/app/${role}`;
              router.navigate([landingPage], { replaceUrl: true });
            }
            return throwError(() => error);
          }),
          catchError((profileError) => {
            // Se il recupero dei profili fallisce, propaga l'errore originale
            return throwError(() => error);
          })
        );
      }

      /**
       * Gestione errori server (>= 500):
       * - Propaga l'errore.
       */
      if (error.status >= 500) {
        return throwError(() => ({
          message: error.error?.message || error.message,
          status: error.status,
          error: error.error,
        }));
      }

      /**
       * Gestione errori di validazione o errori generici con struttura nota.
       */
      if (error.errors || (error.message && error.statusCode)) {
        if (error.errors) {
          // Qui si potrebbero gestire errori di validazione specifici per i campi dei form
          const validationErrors = error.errors;
          Object.keys(validationErrors).forEach((field) => {
            const messages = validationErrors[field];
            // Gestisci gli errori di validazione per ogni campo
          });
        }
        return throwError(() => error);
      }

      /**
       * Gestione fallback per altri errori non previsti.
       */
      return throwError(() => ({
        message: error.error?.message || error.message,
        status: error.status,
        error: error.error,
      }));
    }),
    finalize(() => {
      // Ferma il loading automaticamente al completamento della richiesta
      loadingService.stopLoading();
    })
  );
};
